import React, { useMemo } from 'react';
import type {
  AvailableInvestor,
  ExistingInvestor,
  RecalculationSummary,
} from '../types';

interface UseInvestorSelectionProps {
  availableInvestors: AvailableInvestor[];
  existingInvestors: ExistingInvestor[];
  setAvailableInvestors: React.Dispatch<
    React.SetStateAction<AvailableInvestor[]>
  >;
}

export function useInvestorSelection({
  availableInvestors,
  existingInvestors,
  setAvailableInvestors,
}: UseInvestorSelectionProps) {
  const handleToggleInvestor = (investorId: string) => {
    setAvailableInvestors(
      availableInvestors.map((inv) =>
        inv.investor_id === investorId
          ? { ...inv, isSelected: !inv.isSelected }
          : inv
      )
    );
  };

  const handleToggleAll = () => {
    const allSelected = availableInvestors.every((inv) => inv.isSelected);
    setAvailableInvestors(
      availableInvestors.map((inv) => ({
        ...inv,
        isSelected: !allSelected,
      }))
    );
  };

  const selectedCount = availableInvestors.filter(
    (inv) => inv.isSelected
  ).length;

  // Calculate summary statistics for selected new investors
  const summary = useMemo<RecalculationSummary>(() => {
    const selectedInvestors = availableInvestors.filter(
      (inv) => inv.isSelected
    );

    const totalCommitment = selectedInvestors.reduce(
      (sum, inv) => sum + inv.commitment_amount,
      0
    );

    // Calculate proportional drawdown amount based on existing investors' average
    let averageDrawdownPercentage = 0;
    if (existingInvestors.length > 0) {
      const existingTotalCommitment = existingInvestors.reduce(
        (sum, inv) => sum + inv.commitment_amount,
        0
      );
      const existingTotalDrawdown = existingInvestors.reduce(
        (sum, inv) => sum + inv.drawdown_amount,
        0
      );
      averageDrawdownPercentage =
        existingTotalCommitment > 0
          ? existingTotalDrawdown / existingTotalCommitment
          : 0;
    }

    const estimatedDrawdownAmount = totalCommitment * averageDrawdownPercentage;

    // Existing investors summary
    const existingTotalCommitment = existingInvestors.reduce(
      (sum, inv) => sum + inv.commitment_amount,
      0
    );
    const existingTotalDrawdown = existingInvestors.reduce(
      (sum, inv) => sum + inv.drawdown_amount,
      0
    );

    return {
      newInvestors: {
        count: selectedInvestors.length,
        totalCommitment,
        estimatedDrawdown: estimatedDrawdownAmount,
      },
      existingInvestors: {
        count: existingInvestors.length,
        totalCommitment: existingTotalCommitment,
        totalDrawdown: existingTotalDrawdown,
      },
      combined: {
        totalInvestors: selectedInvestors.length + existingInvestors.length,
        totalCommitment: totalCommitment + existingTotalCommitment,
        totalDrawdown: estimatedDrawdownAmount + existingTotalDrawdown,
      },
      averageDrawdownPercentage,
    };
  }, [availableInvestors, existingInvestors]);

  return {
    handleToggleInvestor,
    handleToggleAll,
    selectedCount,
    summary,
  };
}
